/** Geometry helpers shared by the deck's parts. */

import * as THREE from 'three';

/** A cylinder, with the argument order the deck's parts actually want. */
export const cyl = (
  radiusTop: number,
  radiusBottom: number,
  height: number,
  segments = 48,
  open = false,
): THREE.CylinderGeometry =>
  new THREE.CylinderGeometry(radiusTop, radiusBottom, height, segments, 1, open);

/**
 * An extruded rounded rectangle, laid flat in XZ with its base at y = 0.
 * Used for the plinth, the buttons and the headshell — anything moulded.
 */
export function roundedBoxGeometry(
  w: number,
  d: number,
  h: number,
  radius: number,
  bevel = 0.4,
  curveSegments = 10,
): THREE.ExtrudeGeometry {
  const r = Math.min(radius, w / 2 - 0.01, d / 2 - 0.01);
  const b = Math.min(bevel, h / 2 - 0.01, r - 0.01);
  const hw = w / 2 - r;
  const hd = d / 2 - r;

  const s = new THREE.Shape();
  s.moveTo(-hw, -d / 2);
  s.lineTo(hw, -d / 2);
  s.absarc(hw, -hd, r, -Math.PI / 2, 0, false);
  s.lineTo(w / 2, hd);
  s.absarc(hw, hd, r, 0, Math.PI / 2, false);
  s.lineTo(-hw, d / 2);
  s.absarc(-hw, hd, r, Math.PI / 2, Math.PI, false);
  s.lineTo(-w / 2, -hd);
  s.absarc(-hw, -hd, r, Math.PI, Math.PI * 1.5, false);

  const g = new THREE.ExtrudeGeometry(s, {
    depth: h - b * 2,
    bevelEnabled: true,
    bevelThickness: b,
    bevelSize: b,
    bevelOffset: 0,
    bevelSegments: 4,
    curveSegments,
  });
  g.rotateX(-Math.PI / 2);
  g.translate(0, b, 0);
  g.computeVertexNormals();
  return g;
}

/** An invisible mesh used purely as a raycast target. */
export function hitVolume(geometry: THREE.BufferGeometry): THREE.Mesh {
  return new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ visible: false }));
}
